"use client"

import { useRef } from 'react'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { Heart, Phone, Calendar, MessageCircle, Clock, Stethoscope, Star, Sparkles } from 'lucide-react'
import { openCalendly } from '@/lib/calendly'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'
import { useStaggeredAnimation } from '@/hooks/use-animation-delay'

export function HeroSection() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { ref: contentRef, isIntersecting: isContentVisible } = useIntersectionObserver({ 
    threshold: 0.1, 
    once: true 
  })
  
  // Staggered animation for content items
  const contentItems = useStaggeredAnimation(6, 120, 90)

  return (
    <section 
      ref={containerRef}
      className="hero-signature relative min-h-dvh w-full flex items-center justify-center overflow-hidden bg-gradient-to-b from-cream-light via-cream to-background pt-0 lg:pt-24 pb-0 lg:pb-12"
    >
      {/* Background - matches dev: cream gradient; subtle bottom edge */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal/15 to-transparent" />
      </div>
      <div className="absolute inset-0 z-0 pointer-events-none hero-signature__aurora" aria-hidden />
      <div className="absolute inset-0 z-0 pointer-events-none hero-signature__grain" aria-hidden />

      {/* Mobile background - floating blobs, dots, hearts, sparkles (from dev) */}
      <div className="absolute inset-0 z-0 pointer-events-none lg:hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-teal/20 rounded-full blur-3xl animate-pulse-soft" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-peach/25 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/3 left-1/4 w-60 h-60 bg-cream/40 rounded-full blur-2xl animate-spin" style={{ animationDuration: '15s', animationDelay: '1s' }} />
        <div className="absolute top-20 right-10 w-4 h-4 rounded-full bg-teal/60 animate-float" style={{ animationDuration: '5s' }} />
        <div className="absolute bottom-32 right-16 w-4 h-4 rounded-full bg-peach/60 animate-float" style={{ animationDelay: '1.5s', animationDuration: '4.5s' }} />
        <div className="absolute top-24 right-8 animate-float" aria-hidden>
          <Heart className="w-8 h-8 text-peach/60 fill-peach/30" />
        </div>
        <div className="absolute top-1/3 right-12 animate-float" style={{ animationDelay: '1s', animationDuration: '4s' }} aria-hidden>
          <Sparkles className="w-6 h-6 text-teal/50" />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(91,180,165,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(91,180,165,0.02)_1px,transparent_1px)] bg-[length:40px_40px]" />
      </div>

      {/* Desktop background - floating blobs, dots, star, heart (from dev) */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none hidden lg:block">
        <div className="absolute top-20 left-10 w-64 h-64 bg-teal/10 rounded-full blur-3xl animate-blob animate-pulse-soft" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-peach/20 rounded-full blur-3xl animate-blob animate-pulse-soft" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-cream/50 rounded-full blur-2xl animate-blob animate-pulse-soft" style={{ animationDelay: '4s' }} />
        <div className="absolute top-32 right-20 w-3 h-3 rounded-full bg-peach animate-float hidden sm:block" style={{ animationDuration: '5s' }} />
        <div className="absolute top-48 left-20 w-2 h-2 rounded-full bg-teal animate-float" style={{ animationDelay: '1s', animationDuration: '5s' }} />
        <div className="absolute bottom-1/4 right-1/3 w-3 h-3 rounded-full bg-teal/40 animate-float hidden sm:block" style={{ animationDelay: '2s', animationDuration: '5s' }} />
        <div className="absolute top-32 right-20 hidden sm:block animate-float" style={{ animationDuration: '4s' }} aria-hidden>
          <Star className="w-6 h-6 text-peach opacity-60" />
        </div>
        <div className="absolute bottom-32 left-20 animate-float" style={{ animationDuration: '6s' }} aria-hidden>
          <Heart className="w-5 h-5 text-peach/50 fill-peach/30" />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(91,180,165,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(91,180,165,0.03)_1px,transparent_1px)] bg-[length:60px_60px]" />
      </div>

      <div 
        ref={contentRef}
        className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 h-full flex flex-col justify-center lg:block transition-all duration-300"
      >
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 xl:gap-24 items-center lg:items-stretch h-full">
          {/* Content - Card container on desktop for focus; CTA aligns with credential card bottom */}
          <div className={`text-center lg:text-left order-2 lg:order-2 relative z-20 flex flex-col justify-center h-full lg:min-h-0 pt-6 lg:pt-0 stagger-children max-w-full lg:py-8 lg:pr-1 ${isContentVisible ? 'visible' : ''}`}>
            
            {/* Trust Badge - Minimal pill */}
            <div className={`mt-20 inline-flex items-center gap-2 px-4 py-2 lg:px-4 lg:py-2 rounded-full bg-white/90 text-navy text-xs lg:text-sm font-medium mb-5 lg:mb-6 mx-auto lg:mx-0 border border-slate-200/80 shadow-sm ${contentItems[0] ? 'reveal visible' : 'reveal'}`}>
              <Heart className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-teal fill-teal shrink-0" />
              <span className="font-semibold hidden sm:inline text-center sm:text-left text-slate-700">Trusted by 1000+ families in Jodhpur • Rated 4.9/5 on Google</span>
              <span className="font-semibold sm:hidden text-center sm:text-left text-slate-700">1000+ Families Supported • Rated 4.9/5 on Google</span>
            </div>
            {/* Main Headline - Mobile & Desktop */}
            <h1 className={`font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-[3.225rem] font-bold text-slate-900 leading-[1.2] mb-4 lg:mb-5 tracking-tight ${contentItems[1] ? 'reveal visible' : 'reveal'}`}>
              <span 
                className={`block whitespace-nowrap ${contentItems[1] ? 'reveal visible' : 'reveal'}`}
                style={{ transitionDelay: '100ms' }}
              >
                Pediatrician in Jodhpur
              </span>
              <span 
                className={`block whitespace-nowrap text-teal hero-signature__headline ${contentItems[1] ? 'reveal visible' : 'reveal'}`}
                style={{ transitionDelay: '200ms' }}
              >
                for Newborn & Child Care
              </span>
            </h1>

            

            {/* Empathy Statement - With subtle accent on desktop */}
            <p className={`text-sm sm:text-base lg:text-lg text-slate-600 leading-relaxed mb-5 lg:mb-6 max-w-xl lg:max-w-2xl ${contentItems[2] ? 'reveal visible' : 'reveal'}`}>
            Worried about your baby's health?<br className="sm:hidden" /> You deserve someone who listens, explains clearly, and only prescribes medicines when truly necessary.
            </p>
           
            

            {/* Services Offered - Minimal chips */}
            <div className={`mb-6 ${contentItems[4] ? 'reveal visible' : 'reveal'}`}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Common Concerns We Address</p>
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                <span className="px-3 py-1.5 bg-white text-slate-700 text-xs font-medium rounded-lg border border-slate-200/90 shadow-sm">Fever & Infections</span>
                <span className="px-3 py-1.5 bg-white text-slate-700 text-xs font-medium rounded-lg border border-slate-200/90 shadow-sm">Newborn Care</span>
                <span className="px-3 py-1.5 bg-white text-slate-700 text-xs font-medium rounded-lg border border-peach/30 shadow-sm">Breastfeeding</span>
                <span className="px-3 py-1.5 bg-white text-slate-700 text-xs font-medium rounded-lg border border-slate-200/90 shadow-sm">Sleep Issues</span>
                <span className="px-3 py-1.5 bg-white text-slate-700 text-xs font-medium rounded-lg border border-slate-200/90 shadow-sm">Vaccinations</span>
                <span className="px-3 py-1.5 bg-white text-slate-700 text-xs font-medium rounded-lg border border-slate-200/90 shadow-sm">Nutrition</span>
                <span className="px-3 py-1.5 bg-white text-slate-700 text-xs font-medium rounded-lg border border-slate-200/90 shadow-sm hidden sm:inline-flex">Growth Tracking</span>
                <span className="px-3 py-1.5 bg-white text-slate-700 text-xs font-medium rounded-lg border border-slate-200/90 shadow-sm hidden sm:inline-flex">Allergies & Rashes</span>
              </div>
            </div>

            {/* Call to Action - Glass card; bottom-aligns with doctor credential card on desktop */}
            <div className={`space-y-3 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-white/90 via-cream/20 to-white/90 backdrop-blur-md border-2 border-white/50 shadow-xl lg:shadow-2xl hover:shadow-3xl transition-all duration-300 p-4 lg:p-6 mb-10 lg:mb-0 lg:mt-auto ${contentItems[5] ? 'reveal visible' : 'reveal'}`}>
              <p className="text-sm font-semibold text-slate-700 mb-3">
                One place for all your child's health needs.
              </p>
              
              <div className="flex flex-col lg:flex-row gap-3 justify-center lg:justify-start">
                {/* Mobile: Message Us & Call Now - Side by Side */}
                <div className="flex gap-2 lg:hidden">
                  <a
                    href="https://wa.me/919136486156?text=Hello%20Dr.%20Arushi,%20I%20would%20like%20to%20book%20an%20appointment"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                    aria-label="Message Us on WhatsApp to book an appointment with Dr. Arushi"
                  >
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-teal to-teal-dark hover:from-teal-dark hover:to-teal text-white rounded-full px-4 h-11 text-sm font-bold transition-all duration-300 hover:shadow-2xl hover:shadow-teal/40 w-full border-2 border-teal-dark group"
                    >
                      <MessageCircle className="w-4 h-4 mr-1.5 group-hover:scale-110 transition-transform" />
                      Message Us
                    </Button>
                  </a>

                  <a
                    href="tel:+919136486156"
                    className="flex-1"
                    aria-label="Call Now to book an appointment with Dr. Arushi"
                  >
                    <Button
                      size="lg"
                      variant="outline"
                      className="bg-white hover:bg-teal/5 text-teal-dark hover:text-teal-dark rounded-full px-4 h-11 text-sm font-semibold border-2 border-teal/40 transition-all duration-300 hover:shadow-lg hover:border-teal-dark w-full group"
                    >
                      <Phone className="w-4 h-4 mr-1.5 group-hover:scale-110 transition-transform" />
                      Call Now
                    </Button>
                  </a>
                </div>

                {/* Desktop: Call CTA (Primary) - Hidden on mobile */}
                <a
                  href="tel:+919136486156"
                  className="hidden lg:block"
                  aria-label="Call Now to book an appointment with Dr. Arushi"
                >
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-teal to-teal-dark hover:from-teal-dark hover:to-teal text-white rounded-full px-8 h-12 text-base font-bold transition-all duration-300 hover:shadow-2xl hover:shadow-teal/40 hover:scale-105 border-2 border-teal-dark group whitespace-nowrap"
                  >
                    <Phone className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                    Call Now
                  </Button>
                </a>

                {/* Desktop: Video Consult (Secondary) - Hidden on mobile, shown on desktop */}
                <Button
                  onClick={() => {
                    void openCalendly()
                  }}
                  size="lg"
                  variant="outline"
                  className="hidden lg:flex bg-white hover:bg-teal/5 text-teal-dark hover:text-teal-dark rounded-full px-8 h-12 text-base font-semibold border-2 border-teal/40 transition-all duration-300 hover:shadow-lg hover:border-teal-dark group whitespace-nowrap"
                  aria-label="Video Consult with Dr. Arushi"
                >
                  <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Video Consult
                </Button>

                {/* Mobile: Video Consult - Full width below */}
                <Button
                  onClick={() => {
                    void openCalendly()
                  }}
                  size="lg"
                  variant="outline"
                  className="lg:hidden bg-white hover:bg-teal/5 text-teal-dark hover:text-teal-dark rounded-full px-6 h-11 text-sm font-semibold border-2 border-teal/40 transition-all duration-300 hover:shadow-lg hover:border-teal-dark w-full group"
                  aria-label="Video Consult with Dr. Arushi"
                >
                  <Calendar className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                  Video Consult
                </Button>
              </div>

              {/* Availability - Clean badges */}
              <div className="flex flex-col gap-2 mt-4">
                <div className="flex flex-wrap items-center gap-2 justify-center lg:justify-start">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-lg border border-emerald-200/80">
                    <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full animate-pulse" aria-hidden />
                    <span className="text-xs font-semibold text-emerald-800">Available Today</span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-lg border border-slate-200/80">
                    <Clock className="w-3 h-3 text-slate-600" />
                    <span className="text-xs font-semibold text-slate-700">Evening Hours</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center lg:text-left pb-2 lg:pb-0">Walk-ins welcome • Same-day appointments • Online consultations</p>
              </div>
            </div>
          </div>

          {/* Doctor Image Area - Clean, professional; bottom of credential card aligns with CTA on desktop */}
          <div className={`relative order-1 lg:order-1 mt-16 lg:mt-0 flex justify-center lg:h-full lg:flex-col lg:items-end lg:justify-end lg:min-h-0 lg:pb-14 ${isContentVisible ? 'reveal visible' : 'reveal'}`}>
            <div className="relative w-full max-w-md lg:max-w-lg lg:shrink-0">
              {/* Single soft gradient behind image */}
              <div className="absolute inset-0 top-1/4 bg-gradient-to-b from-teal/10 via-transparent to-transparent rounded-full blur-2xl" />
              
              <div className="relative group">
                <div className="relative aspect-[3/4] flex items-end justify-center transition-all duration-500 group-hover:scale-105">
                  <Image
                    src="/images/doctor.png"
                    alt="Dr. Arushi Singhal Joshi, pediatrician in Jodhpur"
                    fill
                    className="object-contain object-bottom drop-shadow-xl group-hover:drop-shadow-[0_25px_25px_rgba(91,180,165,0.3)] transition-all duration-500"
                    priority
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-teal/20 via-transparent to-peach/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full blur-3xl pointer-events-none" aria-hidden />
                </div>

                {/* Decorative floating icons - from dev (desktop only) */}
                <div className="hidden lg:block">
                  <div
                    className="absolute top-8 right-8 w-16 h-16 bg-gradient-to-br from-peach/90 to-peach-light/90 backdrop-blur-sm rounded-full shadow-xl flex items-center justify-center animate-float group-hover:scale-125 group-hover:rotate-12 transition-all duration-300"
                    style={{ animationDuration: '4s' }}
                    aria-hidden
                  >
                    <Heart className="w-8 h-8 text-white fill-white group-hover:animate-pulse" />
                  </div>
                  <div
                    className="absolute top-16 left-4 w-14 h-14 bg-gradient-to-br from-teal/90 to-teal-light/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center animate-float group-hover:scale-125 group-hover:-rotate-12 transition-all duration-300"
                    style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}
                    aria-hidden
                  >
                    <Star className="w-7 h-7 text-white fill-white group-hover:animate-spin" style={{ animationDuration: '1s' }} />
                  </div>
                  <div
                    className="absolute top-1/2 right-4 w-12 h-12 bg-gradient-to-br from-cream to-peach-light/80 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center animate-float group-hover:scale-125 transition-all duration-300"
                    style={{ animationDuration: '3s', animationDelay: '1s' }}
                    aria-hidden
                  >
                    <Sparkles className="w-6 h-6 text-teal group-hover:animate-pulse" />
                  </div>
                  <div
                    className="absolute bottom-1/3 left-8 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center animate-float group-hover:scale-125 group-hover:rotate-6 transition-all duration-300"
                    style={{ animationDuration: '3.5s', animationDelay: '1.5s' }}
                    aria-hidden
                  >
                    <Heart className="w-5 h-5 text-peach fill-peach group-hover:animate-pulse" />
                  </div>
                </div>

                {/* Doctor Info Card - Translucent glass card (matches dev) */}
                <div className={`absolute -bottom-3 lg:-bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-0.5rem)] lg:w-full max-w-xl bg-gradient-to-br from-white/95 via-cream/30 to-white/95 backdrop-blur-md rounded-2xl lg:rounded-3xl p-4 lg:p-6 shadow-2xl border-2 border-white/50 hover:shadow-3xl transition-all duration-300 ${contentItems[2] ? 'reveal visible' : 'reveal'}`}>
                  <div className="flex items-start gap-3 lg:gap-4">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-gradient-to-br from-teal to-teal-dark flex items-center justify-center shadow-md flex-shrink-0">
                      <Stethoscope className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl sm:text-2xl lg:text-3xl font-serif font-bold text-slate-900 leading-tight tracking-tight mb-1 lg:mb-1.5">
                        Dr. Arushi Singhal Joshi
                      </h2>
                      <p className="text-sm sm:text-base lg:text-lg font-semibold text-teal leading-tight mb-2">
                        Pediatrician & Lactation Consultant
                      </p>
                      <p className="text-xs sm:text-sm lg:text-base text-slate-600 font-medium tracking-wide leading-tight mb-0.5 lg:mb-1">
                        MBBS, MD (Pediatrics), IBCLC
                      </p>
                      <p className="text-xs lg:text-sm text-slate-500 font-medium leading-tight">
                        Internationally Certified Breastfeeding Specialist
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
